import { Stack } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Communication, Reference } from '@medplum/fhirtypes';
import { useState } from 'react';
import { HighlightableItem } from './HighlightableItem';
import { useTimelineNote } from '@/hooks/useTimelineNote';
import { TimelineNote } from '../types';
import { TimelineNoteDisplay } from '@/components/Timeline/TimelineNotes/TimelineNoteDisplay';
import { TimelineNoteEditInput } from '@/components/Timeline/TimelineNotes/TimelineNoteEditInput';
import { JSONContent } from '@tiptap/react';

interface TimelineNoteProps {
  notes: TimelineNote[];
  highlightRef?: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  searchTerm?: string;
}

export function TimelineNotes({
  notes,
  highlightRef,
  copyDirectLink,
  hasActiveFilters,
  handleNavigateToItem,
  searchTerm,
}: TimelineNoteProps) {
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const { isEditingNote, isDeletingNote, editNote, deleteNote } = useTimelineNote();

  if (notes.length === 0) return null;

  const handleEditNote = (note: Communication) => {
    setEditingNoteId(getReferenceString(note)!);
  };

  const handleSaveEdit = async (
    noteReference: Reference<Communication>,
    content: string,
    richTextContent?: JSONContent
  ) => {
    const success = await editNote(noteReference, content, richTextContent);
    if (success) {
      setEditingNoteId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
  };

  const handleDeleteNote = async (noteReference: Reference<Communication>) => {
    await deleteNote(noteReference);
  };

  return (
    <Stack gap="xs" mt="sm">
      {notes.map((note) => {
        const noteRefString = getReferenceString(note.reference)!;
        const isEditing = editingNoteId === noteRefString;

        return (
          <HighlightableItem key={noteRefString} itemRef={noteRefString} highlightRef={highlightRef}>
            {isEditing ? (
              <TimelineNoteEditInput
                initialContent={note.content || ''}
                initialRichTextContent={note.richTextContent}
                onSave={(content, richTextContent) => handleSaveEdit(note.reference, content, richTextContent)}
                onCancel={handleCancelEdit}
                isLoading={isEditingNote}
              />
            ) : (
              <TimelineNoteDisplay
                note={note}
                onEdit={handleEditNote}
                onDelete={handleDeleteNote}
                copyDirectLink={copyDirectLink}
                isDeletingNote={isDeletingNote}
                hasActiveFilters={hasActiveFilters}
                handleNavigateToItem={handleNavigateToItem}
                searchTerm={searchTerm}
              />
            )}
          </HighlightableItem>
        );
      })}
    </Stack>
  );
}
